import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { showSuccess, showError } from "../../v2/utils/toast.ts";
import { API_EXTEND_DEADLINE } from "../../config/Endpoints";
import { postApi } from "../../utils/api";
import * as Yup from "yup";
import { ThreeDots } from "react-loader-spinner";
import { Button, Select, TextArea, TextField } from "../../v2/components/ui/index.ts";
import { checkNativeFormValidity } from "../../v2/utils/form-native-validity.ts";

function getApiErrorMessage(error, fallback = "Something went wrong") {
  const msg = error?.response?.data?.message;
  if (typeof msg === "string" && msg.trim() !== "") return msg;
  if (typeof error?.message === "string" && error.message.trim() !== "")
    return error.message;
  return fallback;
}

const WFHTimesheetHRRequestsModal = ({ timesheet, onClose, refreshList }) => {
  const [action, setAction] = useState("");
  const [extraDays, setExtraDays] = useState("");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({});
  const accessToken = Cookies.get("accessToken");
  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    action: Yup.string().required("Action is required"),
    extraDays: Yup.string().when("action", {
      is: "APPROVED",
      then: (schema) =>
        schema
          .trim()
          .test(
            "range",
            "Extra days must be a whole number between 1 and 24.",
            (val) => {
              if (val == null || val === "") return false;
              const n = Number(val);
              return Number.isInteger(n) && n >= 1 && n <= 24;
            },
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    reason: Yup.string()
      .trim()
      .min(5, "Reason must be at least 5 characters long.")
      .required("Reason is required"),
  });

  useEffect(() => {
    const modalEl = document.getElementById("wfhTimesheetHRRequestsModal");

    if (modalEl) {
      const handleHidden = () => {
        handleReset();
        setErrors({});
      };

      modalEl.addEventListener("hidden.bs.modal", handleHidden);

      return () => {
        modalEl.removeEventListener("hidden.bs.modal", handleHidden);
      };
    }
  }, []);

  const handleReset = () => {
    setAction("");
    setExtraDays("");
    setReason("");
    setErrors({});
  };

  const closeModal = () => {
    const modalEl = document.getElementById("wfhTimesheetHRRequestsModal");
    if (modalEl) {
      const bsModal = window.bootstrap?.Modal?.getInstance(modalEl);
      if (bsModal) {
        bsModal.hide();
      } else {
        modalEl.classList.remove("show");
        modalEl.style.display = "none";
      }
    }

    // Bootstrap sometimes leaves the backdrop div and body classes behind
    // when the modal instance is out of sync — remove them manually.
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("padding-right");

    handleReset();
    onClose();
  };

  const handleSubmit = async (e) => {
    const form = e.currentTarget;
    if (!checkNativeFormValidity(e)) return;
    e.preventDefault();

    try {
      setErrors({});
      await validationSchema.validate(
        { action, extraDays, reason },
        { abortEarly: false },
      );
      setIsLoading(true);

      const payload = {
        employeeId: timesheet.employeeId,
        workDate: timesheet.requestedFor ?? timesheet.date,
        status: action,
        extraDays: action === "APPROVED" ? Number(extraDays) : 0,
        reason,
      };

      const response = await postApi(API_EXTEND_DEADLINE, payload, accessToken);

      showSuccess(
        response?.data?.message ||
          (action === "APPROVED"
            ? "Timesheet deadline extended successfully!"
            : "Timesheet extension request rejected successfully!"),
      );

      refreshList();
      closeModal();
    } catch (error) {
      if (error.inner) {
        const errs = {};
        error.inner.forEach((err) => (errs[err.path] = err.message));
        setErrors(errs);
        setTimeout(() => {
          const invalid = form.querySelector(":invalid");
          if (invalid instanceof HTMLElement) {
            invalid.reportValidity();
          }
        }, 0);
      } else {
        showError(getApiErrorMessage(error));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Modal Header */}
      <div className="modal-header">
        <h5 className="modal-title w-100 text-center">Extension Details</h5>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
          onClick={handleReset}
        ></button>
      </div>

      {/* Modal Body */}
      <div className="modal-body p-4">
        <form onSubmit={handleSubmit}>
          <div className="row col-md-12">
            <div className="col-md-2"></div>
            <div className="col-md-8">
              <div className="mt-2">
                <Select
                  id="wfh-hr-action"
                  label="Action"
                  labelLayout="inline"
                  value={action}
                  required
                  onChange={(value) => {
                    setAction(value);
                    if (value === "REJECTED") {
                      setExtraDays("");
                    }
                    setErrors((prev) => {
                      if (prev.action == null && prev.extraDays == null) return prev;
                      const next = { ...prev };
                      delete next.action;
                      if (value === "REJECTED") delete next.extraDays;
                      return next;
                    });
                  }}
                  options={[
                    { value: "APPROVED", label: "Approve" },
                    { value: "REJECTED", label: "Reject" },
                  ]}
                  placeholder="Select Action"
                  error={errors.action}
                />
              </div>

              <div className="mt-3">
                <TextField
                  id="wfh-hr-extra-days"
                  name="extraDays"
                  label="Extra Days"
                  labelLayout="inline"
                  type="number"
                  value={extraDays}
                  min={1}
                  max={24}
                  required={action === "APPROVED"}
                  disabled={action === "REJECTED"}
                  error={errors.extraDays}
                  showErrorMessage={false}
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    let value = e.target.value;

                    const clearExtraDaysError = () =>
                      setErrors((prev) => {
                        if (prev.extraDays == null) return prev;
                        const next = { ...prev };
                        delete next.extraDays;
                        return next;
                      });

                    if (value === "") {
                      setExtraDays("");
                      clearExtraDaysError();
                      return;
                    }

                    value = value.replace(/[^0-9]/g, "").slice(0, 2);

                    const num = Math.max(0, Math.min(24, Number(value)));

                    setExtraDays(String(num));
                    clearExtraDaysError();
                  }}
                />
              </div>

              <div className="mt-3">
                <TextArea
                  id="wfh-hr-extension-reason"
                  label="Reason"
                  labelLayout="inline"
                  rows={3}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setErrors((prev) => {
                      if (prev.reason == null) return prev;
                      const next = { ...prev };
                      delete next.reason;
                      return next;
                    });
                  }}
                  required
                  error={errors.reason}
                />
              </div>
            </div>
            <div className="col-md-2"></div>
          </div>

          <div className="d-flex gap-2 justify-content-center mt-4">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <ThreeDots
                  height="20"
                  width="53"
                  radius="9"
                  color="white"
                  ariaLabel="three-dots-loading"
                  visible={true}
                />
              ) : (
                "Submit"
              )}
            </Button>
            <Button
              type="button"
              variant="muted-outline"
              data-bs-dismiss="modal"
              onClick={handleReset}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default WFHTimesheetHRRequestsModal;
